<template>
  <div class="request-monitor">
    <header class="rm-header">
      <div>
        <p class="eyebrow">PathTracer administration</p>
        <h1>Request Monitor</h1>
        <p class="subtitle">Review legacy requests, V2 processing, saved results, and CSV batches.</p>
      </div>

      <div class="toolbar">
        <div class="input-group">
          <input
            v-model="search"
            type="search"
            class="input"
            placeholder="Search user, request, transaction, address, or batch…"
            @keyup.enter="applySearch"
            aria-label="Search requests"
          />
          <button class="btn btn--ghost" type="button" @click="applySearch">Search</button>
        </div>

        <select v-model="systemFilter" class="select" aria-label="Filter by request system">
          <option value="all">Legacy and V2</option>
          <option value="legacy">Legacy only</option>
          <option value="v2">V2 only</option>
        </select>

        <select v-model="statusFilter" class="select" aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="queued">Queued</option>
          <option value="running">Running</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="guardrail">Needs review</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <button class="btn" type="button" @click="refresh" :disabled="isLoading">
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </header>

    <p v-if="statusMessage" :class="['status', statusType]" role="status">
      {{ statusMessage }}
    </p>

    <section class="monitor-card">
      <header class="section-head">
        <div>
          <h2>Requests</h2>
          <p>{{ total }} matching request{{ total === 1 ? '' : 's' }}</p>
        </div>
      </header>

      <div v-if="requests.length" class="table-wrap">
        <table class="rm-table">
          <thead>
            <tr>
              <th>System</th>
              <th>Request</th>
              <th>User</th>
              <th>Transaction</th>
              <th>Processing</th>
              <th>Status</th>
              <th>Requested / Updated</th>
              <th>Batch</th>
              <th>Bug</th>
              <th>Result</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="request in requests"
              :key="requestKey(request)"
              :class="rowClass(request)"
            >
              <td>
                <span :class="['system-badge', isV2(request) ? 'system-badge--v2' : 'system-badge--legacy']">
                  {{ isV2(request) ? 'V2' : 'Legacy' }}
                </span>
              </td>

              <td>
                <strong class="mono">{{ request.display_id || request.request_id || '—' }}</strong>
                <small v-if="isV2(request)" class="muted mono" :title="request.request_uuid">
                  {{ request.network || 'unknown' }}
                </small>
              </td>

              <td>
                <strong>{{ request.username || '—' }}</strong>
                <small v-if="request.user_id" class="muted">User {{ request.user_id }}</small>
              </td>

              <td>
                <div class="copy-line">
                  <span class="mono truncate" :title="request.transaction_hash">
                    {{ shorten(request.transaction_hash, 22) }}
                  </span>
                  <button class="icon-btn" type="button" @click="copy(request.transaction_hash)" title="Copy transaction hash">Copy</button>
                </div>
                <div v-if="request.to_address" class="copy-line copy-line--secondary">
                  <span class="mono truncate" :title="request.to_address">
                    To {{ shorten(request.to_address, 18) }}
                  </span>
                  <button class="icon-btn" type="button" @click="copy(request.to_address)" title="Copy destination">Copy</button>
                </div>
              </td>

              <td>
                <strong>{{ processingLabel(request) }}</strong>
                <small class="muted">{{ request.request_type || 'single' }}</small>
                <small v-if="isV2(request) && request.credits_charged !== null" class="muted">
                  {{ request.credits_charged }} credit{{ Number(request.credits_charged) === 1 ? '' : 's' }}
                  · {{ request.charge_status || '—' }}
                </small>
              </td>

              <td>
                <span :class="['pill', pillClass(request)]">{{ statusLabel(request) }}</span>
                <span v-if="isStale(request)" class="pill pill--warn">stale</span>
                <div v-if="isV2(request)" class="progress-row">
                  <div class="progress-track"><span :style="{ width: `${progressPercent(request)}%` }"></span></div>
                  <small>{{ progressPercent(request) }}%</small>
                </div>
                <small v-if="statusText(request)" class="muted status-detail">{{ statusText(request) }}</small>
              </td>

              <td>
                <span>{{ formatTime(request.request_time || request.created_at) }}</span>
                <small class="muted">Updated {{ formatTime(request.updated_at || request.request_time) }}</small>
                <small v-if="request.lease_active" class="lease-chip">Active lease</small>
              </td>

              <td>
                <template v-if="request.batch_uuid">
                  <strong class="mono">{{ String(request.batch_uuid).slice(0, 8) }}</strong>
                  <small class="muted">{{ request.batch_source_name || request.batch_status || 'CSV batch' }}</small>
                </template>
                <span v-else>—</span>
              </td>

              <td>
                <span v-if="request.bug_reported === 'yes' || request.bug_reported === true" class="bug-chip">
                  {{ request.bug_status === 'resolved' ? 'Resolved' : 'Reported' }}
                </span>
                <span v-else>—</span>
              </td>

              <td>
                <div class="action-stack">
                  <button
                    v-if="isV2(request)"
                    class="btn btn--ghost small"
                    type="button"
                    @click="viewRequest(request, 'visual')"
                  >
                    {{ normalizeStatus(request.status) === 'completed' ? 'View visual' : 'View progress' }}
                  </button>
                  <button
                    v-if="isV2(request) && normalizeStatus(request.status) === 'completed'"
                    class="btn btn--ghost small"
                    type="button"
                    @click="viewRequest(request, 'report')"
                  >
                    View report
                  </button>
                  <button
                    v-if="!isV2(request)"
                    class="btn btn--ghost small"
                    type="button"
                    @click="viewRequest(request, 'visual')"
                  >
                    View screenshot
                  </button>
                  <details v-if="!isV2(request) && request.result" class="inline-result">
                    <summary>Legacy result</summary>
                    <pre>{{ request.result }}</pre>
                  </details>
                </div>
              </td>

              <td>
                <button
                  class="btn btn--reset small"
                  type="button"
                  :disabled="isResetting(request)"
                  @click="confirmReset(request)"
                >
                  {{ isResetting(request) ? 'Resetting…' : (isV2(request) ? 'Reset & reprocess' : 'Requeue') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p v-else class="empty">No requests matched the current filters.</p>

      <div class="pagination">
        <button class="btn btn--ghost" type="button" @click="previousPage" :disabled="page <= 1">Previous</button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button class="btn btn--ghost" type="button" @click="nextPage" :disabled="page >= totalPages">Next</button>
      </div>
    </section>

    <section v-if="systemFilter !== 'legacy'" class="monitor-card monitor-card--batches">
      <header class="section-head">
        <div>
          <h2>Recent CSV batches</h2>
          <p>Persistent V2 queue manifests and their linked request progress.</p>
        </div>
      </header>

      <div v-if="batches.length" class="batch-grid">
        <article v-for="batch in batches" :key="batch.batch_uuid" class="batch-card">
          <div class="batch-card__head">
            <div>
              <span class="system-badge system-badge--v2">V2 batch</span>
              <strong class="mono">{{ String(batch.batch_uuid).slice(0, 8) }}</strong>
            </div>
            <span :class="['pill', batchPillClass(batch)]">{{ batchStatusLabel(batch.status) }}</span>
          </div>
          <h3>{{ batch.source_name || 'CSV Helper batch' }}</h3>
          <p>{{ batch.username }} · {{ batch.completed_items }} of {{ batch.total_items }} complete</p>
          <div class="progress-track progress-track--batch">
            <span :style="{ width: `${batchProgress(batch)}%` }"></span>
          </div>
          <dl class="batch-stats">
            <div><dt>Active</dt><dd>{{ batch.active_items }}</dd></div>
            <div><dt>Waiting</dt><dd>{{ batch.waiting_items }}</dd></div>
            <div><dt>Attention</dt><dd>{{ batch.attention_items }}</dd></div>
            <div><dt>Credits</dt><dd>{{ batch.actual_credits_charged }} / {{ batch.estimated_credit_cost }}</dd></div>
          </dl>
          <small class="muted">Updated {{ formatTime(batch.updated_at) }}</small>
        </article>
      </div>
      <p v-else class="empty">No V2 batches are available.</p>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

const V2_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export default {
  name: 'RequestMonitor',
  data() {
    return {
      requests: [],
      batches: [],
      resetting: {},
      statusMessage: '',
      statusType: '',
      page: 1,
      limit: 20,
      total: 0,
      isLoading: false,
      search: '',
      systemFilter: 'all',
      statusFilter: 'all',
      lastQueryStamp: 0,
      pollTimer: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    },
  },
  watch: {
    statusFilter() {
      this.page = 1;
      void this.fetchRequests();
    },
    systemFilter() {
      this.page = 1;
      void this.fetchRequests();
    },
  },
  mounted() {
    void this.fetchRequests();
    this.pollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !this.isLoading) {
        void this.fetchRequests(false);
      }
    }, 10000);
  },
  beforeUnmount() {
    if (this.pollTimer) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
  },
  methods: {
    isV2(request) {
      return request?.request_system === 'casebuddy_lite'
        || request?.request_version === 'v2'
        || V2_UUID.test(String(request?.request_uuid || ''));
    },
    requestKey(request) {
      return request?.history_key
        || (this.isV2(request)
          ? `casebuddy_lite:${request.request_uuid}`
          : `legacy:${request.request_id}`);
    },
    normalizeStatus(value) {
      const status = String(value || '').trim().toLowerCase();
      if (status === 'complete') return 'completed';
      if (['in progress', 'pending'].includes(status)) return 'running';
      if (status === 'error') return 'failed';
      return status;
    },
    statusLabel(request) {
      const labels = {
        queued: 'Queued',
        running: 'Running',
        paused: 'Paused',
        completed: 'Complete',
        guardrail: 'Needs review',
        failed: 'Failed',
        cancelled: 'Cancelled',
      };
      const status = this.normalizeStatus(request?.status);
      return labels[status] || request?.status || 'Unknown';
    },
    statusText(request) {
      if (!this.isV2(request)) return '';
      const status = this.normalizeStatus(request?.status);
      if (status === 'running') return 'Tracing transactions';
      if (status === 'queued') return 'Ready to process';
      if (status === 'paused') return 'Ready to continue';
      if (status === 'completed') return 'Trace completed';
      if (status === 'guardrail') return 'Review needed';
      if (status === 'failed') return 'Needs attention';
      return String(request?.status_message || '');
    },
    pillClass(request) {
      const status = this.normalizeStatus(request?.status);
      if (status === 'completed') return 'pill--ok';
      if (status === 'failed') return 'pill--error';
      if (status === 'guardrail') return 'pill--warn';
      if (status === 'running') return 'pill--progress';
      if (status === 'paused') return 'pill--paused';
      if (status === 'cancelled') return 'pill--muted';
      return 'pill--queued';
    },
    batchPillClass(batch) {
      return this.pillClass({ status: batch?.status });
    },
    batchStatusLabel(status) {
      const value = String(status || '').toLowerCase();
      const labels = {
        queued: 'Queued',
        running: 'Running',
        paused: 'Paused',
        completed: 'Complete',
        partial: 'Needs attention',
        cancelled: 'Cancelled',
        failed: 'Failed',
      };
      return labels[value] || status || 'Unknown';
    },
    progressPercent(request) {
      const value = Number(request?.progress_percent || 0);
      return Number.isFinite(value)
        ? Math.round(Math.max(0, Math.min(100, value)))
        : 0;
    },
    batchProgress(batch) {
      const total = Number(batch?.total_items || 0);
      const complete = Number(batch?.completed_items || 0);
      if (total <= 0) return 0;
      return Math.round(Math.max(0, Math.min(100, (complete / total) * 100)));
    },
    processingLabel(request) {
      const hop = this.formatHopLogic(request?.hop_logic);
      if (this.isV2(request)) {
        return [request?.network || 'unknown', hop].filter(Boolean).join(' · ');
      }
      return [request?.mode || '', hop].filter(Boolean).join(' · ');
    },
    formatHopLogic(value) {
      const normalized = String(value || 'fifo').toLowerCase().trim();
      if (normalized === 'lifo') return 'LIFO';
      if (['next_out', 'next_chronological'].includes(normalized)) return 'Next chronological';
      return 'FIFO';
    },
    shorten(value, length = 20) {
      const text = String(value || '');
      if (text.length <= length) return text || '—';
      const left = Math.max(5, Math.floor((length - 3) / 2));
      const right = Math.max(4, length - 3 - left);
      return `${text.slice(0, left)}...${text.slice(-right)}`;
    },
    formatTime(value) {
      if (!value) return '—';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
    },
    copy(value) {
      if (!value) return;
      navigator.clipboard?.writeText(String(value)).catch(() => {});
    },
    isStale(request) {
      const status = this.normalizeStatus(request?.status);
      if (!['queued', 'running'].includes(status)) return false;
      const date = new Date(request?.updated_at || request?.request_time || 0);
      return Number.isFinite(date.getTime())
        && date.getTime() < Date.now() - 10 * 60 * 1000;
    },
    rowClass(request) {
      const status = this.normalizeStatus(request?.status);
      if (status === 'failed') return 'row row--error';
      if (this.isStale(request)) return 'row row--stale';
      return 'row';
    },
    requestResetKey(request) {
      return this.requestKey(request);
    },
    isResetting(request) {
      return Boolean(this.resetting[this.requestResetKey(request)]);
    },
    async confirmReset(request) {
      const isV2 = this.isV2(request);
      const identifier = request.display_id || request.request_id || String(request.request_uuid || '').slice(0, 8);
      const message = isV2
        ? `Reset V2 request ${identifier} from scratch?\n\nThe saved result, visual, checkpoint, lease, and Tool 88 jobs will be cleared. The original paid or exempt entitlement remains, and the user will not be charged again.`
        : `Requeue legacy request ${identifier}?\n\nIts worker lease and attempt/error state will be cleared.`;
      if (!window.confirm(message)) return;
      await this.resetRequest(request);
    },
    async resetRequest(request) {
      const key = this.requestResetKey(request);
      this.resetting = { ...this.resetting, [key]: true };
      try {
        const token = localStorage.getItem('token') || '';
        const payload = this.isV2(request)
          ? {
              request_system: 'casebuddy_lite',
              request_uuid: request.request_uuid,
            }
          : {
              request_system: 'legacy',
              request_id: request.request_id,
            };
        const response = await axios.post(
          'etherscan/requeue_request.php',
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.status !== 'success') {
          throw new Error(response.data?.message || 'Request reset failed.');
        }
        this.statusMessage = this.isV2(request)
          ? `V2 request ${request.display_id || String(request.request_uuid).slice(0, 8)} was reset without another credit charge.`
          : `Legacy request ${request.request_id} was requeued.`;
        this.statusType = 'success';
        await this.fetchRequests(false);
      } catch (error) {
        this.statusMessage = error?.response?.data?.message || error?.message || 'Request reset failed.';
        this.statusType = 'error';
      } finally {
        const next = { ...this.resetting };
        delete next[key];
        this.resetting = next;
      }
    },
    viewRequest(request, view = 'visual') {
      const requestID = this.isV2(request)
        ? `v2:${request.request_uuid}`
        : String(request.request_id);
      const route = this.$router.resolve({
        name: 'AdminViewImage',
        params: { requestID },
        query: this.isV2(request) ? { view } : {},
      });
      window.open(route.href, '_blank', 'noopener,noreferrer');
    },
    async refresh() {
      this.page = 1;
      await this.fetchRequests(true);
    },
    applySearch() {
      this.page = 1;
      void this.fetchRequests(true);
    },
    async fetchRequests(showLoading = true) {
      const stamp = Date.now();
      this.lastQueryStamp = stamp;
      if (showLoading) this.isLoading = true;
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Not authenticated.');
        const params = {
          limit: this.limit,
          page: this.page,
          system: this.systemFilter,
        };
        const trimmedSearch = this.search.trim();
        if (trimmedSearch) params.search = trimmedSearch;
        if (this.statusFilter !== 'all') params.status = this.statusFilter;

        const response = await axios.get('etherscan/fetch_all_requests.php', {
          params,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (stamp !== this.lastQueryStamp) return;
        const data = response.data || {};
        if (data.status !== 'success') {
          throw new Error(data.message || 'Unexpected response.');
        }
        this.requests = Array.isArray(data.requests) ? data.requests : [];
        this.batches = Array.isArray(data.batches) ? data.batches : [];
        this.total = Number(data.total || 0);
        if (showLoading) {
          this.statusMessage = '';
          this.statusType = '';
        }
      } catch (error) {
        console.error('RequestMonitor error:', error);
        if (showLoading) {
          this.requests = [];
          this.batches = [];
          this.total = 0;
        }
        this.statusMessage = error?.response?.data?.message || error?.message || 'Failed to fetch requests.';
        this.statusType = 'error';
      } finally {
        if (showLoading) this.isLoading = false;
      }
    },
    async nextPage() {
      if (this.page >= this.totalPages) return;
      this.page += 1;
      await this.fetchRequests(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    async previousPage() {
      if (this.page <= 1) return;
      this.page -= 1;
      await this.fetchRequests(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  },
};
</script>

<style scoped>
.request-monitor {
  max-width: 1560px;
  margin: 0 auto;
  padding: 1rem;
  color: #1f2937;
}
.rm-header {
  display: grid;
  gap: 0.9rem;
  margin-bottom: 1rem;
}
.rm-header h1,
.section-head h2,
.batch-card h3 { margin: 0; color: #0f2740; }
.eyebrow { margin: 0 0 0.2rem; color: #2563eb; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; font-size: 0.76rem; }
.subtitle,
.section-head p,
.batch-card p { margin: 0.25rem 0 0; color: #64748b; }
.toolbar { display: flex; flex-wrap: wrap; gap: 0.55rem; align-items: center; }
.input-group { display: flex; gap: 0.4rem; flex: 1 1 500px; }
.input,
.select { padding: 0.65rem 0.75rem; border-radius: 10px; border: 1px solid #dbe3ee; background: #fff; }
.input { flex: 1; min-width: 260px; }
.select { min-width: 155px; }
.btn { padding: 0.6rem 0.82rem; border-radius: 10px; border: 1px solid rgba(15, 38, 61, 0.15); background: linear-gradient(180deg, #fff, #f3f4f6); cursor: pointer; font-weight: 700; }
.btn:hover { background: linear-gradient(180deg, #f9fafb, #eaf0f7); }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn--ghost { background: #f8fafc; border-color: #dbe3ee; }
.btn--reset { background: #e0edff; color: #174ea6; border-color: #a9c8ff; }
.small { padding: 0.42rem 0.62rem; font-size: 0.85rem; }
.monitor-card { margin-top: 1rem; padding: 1rem; background: #fff; border: 1px solid #dfe7f1; border-radius: 16px; box-shadow: 0 10px 28px rgba(15, 39, 64, 0.07); }
.section-head { display: flex; justify-content: space-between; gap: 1rem; margin-bottom: 0.8rem; }
.table-wrap { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 12px; }
.rm-table { width: 100%; min-width: 1500px; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
.rm-table th { position: sticky; top: 0; z-index: 2; padding: 0.7rem; background: #f3f6fb; text-align: left; color: #0f2740; border-bottom: 1px solid #dbe3ee; }
.rm-table td { padding: 0.7rem; vertical-align: top; border-bottom: 1px solid #edf1f6; overflow-wrap: anywhere; }
.row--error { background: #fff5f5; }
.row--stale { background: #fffaf0; }
.system-badge,
.pill,
.bug-chip,
.lease-chip { display: inline-flex; align-items: center; width: fit-content; border-radius: 999px; padding: 0.18rem 0.5rem; font-size: 0.75rem; font-weight: 800; border: 1px solid transparent; }
.system-badge--legacy { background: #f1f5f9; color: #475569; border-color: #cbd5e1; }
.system-badge--v2 { background: #e0edff; color: #174ea6; border-color: #a9c8ff; }
.pill--ok { background: #e8faf3; color: #065f46; border-color: #a7f3d0; }
.pill--error { background: #fde8e8; color: #991b1b; border-color: #fecaca; }
.pill--queued { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
.pill--progress { background: #ecfeff; color: #155e75; border-color: #a5f3fc; }
.pill--paused { background: #f5f3ff; color: #6d28d9; border-color: #ddd6fe; }
.pill--warn { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.pill--muted { background: #f1f5f9; color: #64748b; border-color: #cbd5e1; }
.bug-chip { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
.lease-chip { margin-top: 0.35rem; background: #ecfeff; color: #155e75; border-color: #a5f3fc; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace; }
.muted { display: block; margin-top: 0.2rem; color: #64748b; font-size: 0.78rem; }
.truncate { display: inline-block; max-width: 190px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; }
.copy-line { display: flex; gap: 0.3rem; align-items: center; }
.copy-line--secondary { margin-top: 0.25rem; color: #64748b; }
.icon-btn { padding: 0.15rem 0.35rem; border: 1px solid #dbe3ee; border-radius: 6px; background: #fff; font-size: 0.7rem; cursor: pointer; }
.action-stack { display: grid; gap: 0.35rem; }
.inline-result summary { cursor: pointer; color: #2563eb; font-weight: 700; }
.inline-result pre { max-width: 280px; max-height: 240px; overflow: auto; white-space: pre-wrap; background: #f8fafc; padding: 0.55rem; border-radius: 8px; }
.progress-row { display: flex; gap: 0.4rem; align-items: center; margin-top: 0.4rem; }
.progress-track { width: 92px; height: 7px; overflow: hidden; border-radius: 999px; background: #e2e8f0; }
.progress-track span { display: block; height: 100%; background: linear-gradient(90deg, #2563eb, #22c55e); }
.status-detail { max-width: 180px; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 0.7rem; margin-top: 1rem; }
.empty { padding: 1.2rem; text-align: center; color: #64748b; }
.status { font-weight: 700; }
.status.success { color: #047857; }
.status.error { color: #b91c1c; }
.batch-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 0.8rem; }
.batch-card { padding: 0.9rem; border: 1px solid #dfe7f1; border-radius: 13px; background: #f8fafc; }
.batch-card__head { display: flex; justify-content: space-between; gap: 0.6rem; align-items: flex-start; }
.batch-card__head > div { display: grid; gap: 0.35rem; }
.progress-track--batch { width: 100%; margin: 0.7rem 0; }
.batch-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.35rem; margin: 0; }
.batch-stats div { padding: 0.45rem; border-radius: 8px; background: #fff; text-align: center; }
.batch-stats dt { color: #64748b; font-size: 0.72rem; }
.batch-stats dd { margin: 0.15rem 0 0; font-weight: 800; }
@media (max-width: 800px) {
  .input-group { flex-basis: 100%; }
  .monitor-card { padding: 0.7rem; }
  .batch-stats { grid-template-columns: repeat(2, 1fr); }
}
</style>
